import { useState, useEffect } from "react";
import { AlertCircle, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";
import api from "../utils/api";

/**
 * Global banner that appears across the app when the user's account is in
 * the GDPR cancellation flow. Dual-state:
 *   - cancelling  → amber: full access, but warns about scheduled deletion
 *   - read_only   → red:   read-only mode, urge reactivate or export
 *
 * Hidden when account is active. Polls /api/gdpr/status on mount + every
 * 5 minutes so transitions from cancelling → read_only show without a refresh.
 */
const CancellationBanner = () => {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [reactivating, setReactivating] = useState(false);

  const fetchStatus = async () => {
    try {
      const r = await api.get("/gdpr/status");
      setStatus(r.data);
    } catch {
      setStatus(null);
    }
  };

  useEffect(() => {
    fetchStatus();
    const t = setInterval(fetchStatus, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const handleReactivate = async () => {
    setReactivating(true);
    try {
      await api.post("/gdpr/reactivate");
      toast({ title: "Account reactivated", description: "Welcome back." });
      await fetchStatus();
      // Soft-reload to refresh any feature gates
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast({
        title: "Couldn't reactivate",
        description: e?.response?.data?.detail || "Try again or contact support.",
        variant: "destructive",
      });
    } finally {
      setReactivating(false);
    }
  };

  if (!status || status.status === "active" || status.status === "purged") return null;

  const isReadOnly = status.status === "read_only";
  const days = status.days_remaining ?? "—";
  const purgeDate = status.purge_at ? new Date(status.purge_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "";

  return (
    <div
      data-testid="cancellation-banner"
      className={`w-full px-4 py-3 flex flex-wrap items-center justify-center gap-3 text-sm shadow-sm ${
        isReadOnly
          ? "bg-red-50 border-b border-red-200 text-red-900"
          : "bg-amber-50 border-b border-amber-200 text-amber-900"
      }`}
    >
      <AlertCircle className={`w-4 h-4 ${isReadOnly ? "text-red-600" : "text-amber-600"} flex-shrink-0`} />
      <span>
        {isReadOnly ? (
          <>
            <strong>Read-only mode.</strong> Your account will be permanently deleted on <strong>{purgeDate}</strong> ({days} days).
          </>
        ) : (
          <>
            <strong>Account deletion scheduled</strong> for <strong>{purgeDate}</strong> ({days} days). You still have full access until your paid period ends.
          </>
        )}
      </span>
      <Button
        size="sm"
        variant={isReadOnly ? "default" : "outline"}
        onClick={handleReactivate}
        disabled={reactivating}
        data-testid="cancellation-banner-reactivate"
        className={isReadOnly ? "bg-red-600 hover:bg-red-700 text-white" : ""}
      >
        {reactivating ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : null}
        Reactivate
      </Button>
    </div>
  );
};

export default CancellationBanner;
